/**
 * 运维变更二次确认（对齐 sandbox/approvalStore 的 Promise 弹层模式）。
 * 流程：展示计划 → 用户输入密码 +「确认执行」→ resolve / reject。
 */

export type MutationPlanView = {
  planId: string;
  risk: string;
  targetId?: string;
  expiresAt?: number;
  analysisExcerpt?: string;
  actions: Array<{
    op: string;
    resource: string;
    namespace?: string;
    summary: string;
  }>;
};

export type MutationApprovalPayload = {
  password: string;
  confirmPhrase: string;
};

type Pending = {
  plan: MutationPlanView;
  resolve: (v: MutationApprovalPayload) => void;
  reject: (e?: Error) => void;
};

let pending: Pending | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of [...listeners]) fn();
}

/** Sheet 挂载时订阅，返回退订函数（给 useEffect cleanup 用） */
export function subscribeMutationApproval(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** 当前挂起的变更计划；同时最多一条（新请求会先拒掉旧的） */
export function getPendingMutation(): Pending | null {
  return pending;
}

/** UI 调用：弹出确认；返回用户输入或抛错取消 */
export function requestMutationApproval(
  plan: MutationPlanView,
): Promise<MutationApprovalPayload> {
  return new Promise((resolve, reject) => {
    // 若已有挂起项，先拒绝旧的，避免叠层
    if (pending) {
      pending.reject(new Error("已有待确认的变更"));
    }
    pending = { plan, resolve, reject };
    emit();
  });
}

/** 结算：ok=true 交付密码+确认短语；否则 reject「用户取消变更」；先清空再回调，防重入 */
export function settleMutationApproval(
  ok: boolean,
  payload?: MutationApprovalPayload,
) {
  if (!pending) return;
  const p = pending;
  pending = null;
  emit();
  if (ok && payload) p.resolve(payload);
  else p.reject(new Error("用户取消变更"));
}
